import {
  ActivityFeedEntityPartsFragment,
  ActivityFeedGroupPartsFragment,
  ActivityFeedPartsFragment,
  CompanyType,
  EntityType,
} from '../../__generated__/graphql'
import { TFunction } from 'i18next'
import {
  AdCopyRouteBuilder,
  AdGroupRouteBuilder,
  AdTemplateRouteBuilder,
  CampaignProgrammaticRouteBuilder,
  CampaignRouteBuilder,
  CompanyRouteBuilder,
  PositionRouteBuilder,
  WebsiteRouteBuilder,
} from '../routeParams'

type ActivityFeedEntity = NonNullable<ActivityFeedPartsFragment['entity']>
export type ActivityFeedResolvedEntity = ActivityFeedEntity
type ActivityFeedCompany = ActivityFeedPartsFragment['company']

const publisherSlugFromCompany = (company: ActivityFeedCompany): string | undefined =>
  company.type === CompanyType.Publisher ? company.slug : undefined

const advertiserSlugFromCompany = (company: ActivityFeedCompany): string | undefined =>
  company.type === CompanyType.Advertiser ? company.slug : undefined

const tradingDeskSlugFromCompany = (company: ActivityFeedCompany): string | undefined =>
  company.type === CompanyType.TradingDesk ? company.slug : undefined

const entityTypeByTypename: Record<ActivityFeedEntity['__typename'], EntityType> = {
  AdCopy: EntityType.AdCopy,
  AdGroup: EntityType.AdGroup,
  AdGroupBudgetPlan: EntityType.AdGroupBudgetPlan,
  AdTemplateBanner: EntityType.AdTemplateBanner,
  AdTemplateNative: EntityType.AdTemplateNative,
  AdTemplateWeb: EntityType.AdTemplateWeb,
  Advertiser: EntityType.Advertiser,
  Agency: EntityType.Agency,
  BillingContact: EntityType.BillingContact,
  Campaign: EntityType.Campaign,
  CampaignProgrammatic: EntityType.ProgrammaticCampaign,
  AdGroupDirectoryPreset: EntityType.AdGroupDirectoryPreset,
  CompanyApiToken: EntityType.CompanyApiToken,
  PublisherExclusionPreset: EntityType.PublisherExclusionPreset,
  Position: EntityType.Position,
  Publisher: EntityType.Publisher,
  Reseller: EntityType.Reseller,
  SegmentBehavioural: EntityType.SegmentBehavioural,
  SegmentCrm: EntityType.Segment,
  TradingDesk: EntityType.TradingDesk,
  User: EntityType.User,
  Website: EntityType.Website,
}

const ACTIVITY_FEED_COMPANY_ENTITY_TYPES = new Set<EntityType>([
  EntityType.Advertiser,
  EntityType.Company,
  EntityType.Publisher,
  EntityType.Reseller,
  EntityType.TradingDesk,
])

export const isActivityFeedCompanyEntityType = (entityType: EntityType | undefined): boolean =>
  entityType != null && ACTIVITY_FEED_COMPANY_ENTITY_TYPES.has(entityType)

const formatEntityReference = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined

  return String(value)
}

const labelFromName = (name: string | null | undefined, id: unknown): string =>
  name?.trim() || formatEntityReference(id) || ''

export function parseActivityFeedEntity(
  entity: ActivityFeedEntityPartsFragment | null | undefined
): ActivityFeedResolvedEntity | null {
  if (!entity) return null

  switch (entity.__typename) {
    case 'AdCopy':
      return {
        __typename: 'AdCopy',
        id: entity.id,
        name: entity.name,
        adGroup: entity.adGroup,
      }
    case 'AdGroup':
      return {
        __typename: 'AdGroup',
        id: entity.id,
        name: entity.name,
        campaign: entity.campaign,
      }
    case 'AdGroupBudgetPlan':
      return {
        __typename: 'AdGroupBudgetPlan',
        id: entity.id,
        adGroupId: entity.adGroupId,
        plannedDate: new Date(entity.plannedDate).toLocaleDateString(),
      }
    case 'AdGroupDirectoryPreset':
      return {
        __typename: 'AdGroupDirectoryPreset',
        id: entity.id,
        name: entity.name,
        companyId: entity.companyId,
      }
    case 'PublisherExclusionPreset':
      return {
        __typename: 'PublisherExclusionPreset',
        id: entity.id,
        name: entity.name,
      }
    case 'AdTemplateBanner':
      return {
        __typename: 'AdTemplateBanner',
        id: entity.id,
        name: entity.name,
        position: entity.position,
      }
    case 'AdTemplateNative':
      return {
        __typename: 'AdTemplateNative',
        id: entity.id,
        name: entity.name,
        position: entity.position,
      }
    case 'AdTemplateWeb':
      return {
        __typename: 'AdTemplateWeb',
        id: entity.id,
        name: entity.name,
        position: entity.position,
      }
    case 'Advertiser':
      return {
        __typename: 'Advertiser',
        id: entity.id,
        name: entity.name,
        slug: entity.slug,
        type: entity.type,
      }
    case 'Agency':
      return {
        __typename: 'Agency',
        id: entity.id,
        name: entity.name,
        slug: entity.slug,
        type: entity.type,
      }
    case 'BillingContact':
      return {
        __typename: 'BillingContact',
        id: entity.id,
        firstName: entity.firstName,
        lastName: entity.lastName,
        companyId: entity.companyId,
      }
    case 'Campaign':
      return {
        __typename: 'Campaign',
        id: entity.id,
        name: entity.name,
        advertiser: entity.advertiser,
      }
    case 'CampaignProgrammatic':
      return {
        __typename: 'CampaignProgrammatic',
        id: entity.id,
        name: entity.name,
        tradingDesk: entity.tradingDesk,
      }
    case 'CompanyApiToken':
      return {
        __typename: 'CompanyApiToken',
        token: entity.token,
      }
    case 'Position':
      return {
        __typename: 'Position',
        id: entity.id,
        name: entity.name,
        website: entity.website,
      }
    case 'Publisher':
      return {
        __typename: 'Publisher',
        id: entity.id,
        name: entity.name,
        slug: entity.slug,
        type: entity.type,
      }
    case 'Reseller':
      return {
        __typename: 'Reseller',
        id: entity.id,
        name: entity.name,
        slug: entity.slug,
        type: entity.type,
      }
    case 'SegmentBehavioural':
      return {
        __typename: 'SegmentBehavioural',
        id: entity.id,
        name: entity.name,
      }
    case 'SegmentCrm':
      return {
        __typename: 'SegmentCrm',
        id: entity.id,
        name: entity.name,
      }
    case 'TradingDesk':
      return {
        __typename: 'TradingDesk',
        id: entity.id,
        name: entity.name,
        slug: entity.slug,
        type: entity.type,
      }
    case 'User':
      return {
        __typename: 'User',
        id: entity.id,
        firstName: entity.firstName,
        lastName: entity.lastName,
      }
    case 'Website':
      return {
        __typename: 'Website',
        id: entity.id,
        name: entity.name,
        publisher: entity.publisher,
      }
    default:
      return null
  }
}

export function parseActivityFeedEntry(feed: ActivityFeedPartsFragment): ActivityFeedPartsFragment {
  const clonedFeed = structuredClone(feed)

  return {
    ...clonedFeed,
    entity: parseActivityFeedEntity(clonedFeed.entity),
    oldValueEntity: parseActivityFeedEntity(clonedFeed.oldValueEntity),
    newValueEntity: parseActivityFeedEntity(clonedFeed.newValueEntity),
  }
}

const parseActivityFeedGroupItem = (
  item: ActivityFeedGroupPartsFragment['items'][number]
): ActivityFeedGroupPartsFragment['items'][number] => ({
  ...item,
  oldValueEntity: parseActivityFeedEntity(item.oldValueEntity),
  newValueEntity: parseActivityFeedEntity(item.newValueEntity),
})

export function parseActivityFeedGroup(group: ActivityFeedGroupPartsFragment): ActivityFeedGroupPartsFragment {
  const clonedGroup = structuredClone(group)

  return {
    ...clonedGroup,
    entity: parseActivityFeedEntity(clonedGroup.entity),
    items: clonedGroup.items.map(parseActivityFeedGroupItem),
  }
}

export function mergeActivityFeedGroupsOnAutoReload(
  currentGroups: ActivityFeedGroupPartsFragment[],
  incomingGroups: ActivityFeedGroupPartsFragment[],
  limit: number
): ActivityFeedGroupPartsFragment[] {
  const incomingByGroupId = new Map(incomingGroups.map((group) => [group.groupId, group]))
  const currentGroupIds = new Set(currentGroups.map((group) => group.groupId))
  const newGroups = incomingGroups.filter((group) => !currentGroupIds.has(group.groupId))
  const updatedCurrentGroups = currentGroups.map((group) => incomingByGroupId.get(group.groupId) ?? group)

  return [...newGroups, ...updatedCurrentGroups].slice(0, limit)
}

export function getActivityFeedEntityType(
  entity: ActivityFeedResolvedEntity | null | undefined,
  fallbackEntityType?: EntityType | null
): EntityType | undefined {
  const parsedEntity = parseActivityFeedEntity(entity)

  if (parsedEntity) return entityTypeByTypename[parsedEntity.__typename]

  return fallbackEntityType ?? undefined
}

export const getActivityFeedEntityTypeLabel = (
  entityType: EntityType | undefined,
  t: TFunction
): string | undefined => {
  if (!entityType) return undefined

  return t(`enum.EntityType.${entityType}`, { defaultValue: entityType })
}

export const getActivityFeedEntityTypeLabelInSentence = (entityType: EntityType | undefined, t: TFunction): string => {
  const label = getActivityFeedEntityTypeLabel(entityType, t)

  if (!label) return ''

  return label.charAt(0).toLocaleLowerCase() + label.slice(1)
}

export function getActivityFeedEntityId(entity: ActivityFeedResolvedEntity | null | undefined): unknown | undefined {
  const parsedEntity = parseActivityFeedEntity(entity)

  if (!parsedEntity || parsedEntity.__typename === 'CompanyApiToken') return undefined

  return parsedEntity.id
}

export function getActivityFeedEntityLabel(entity: ActivityFeedResolvedEntity | null | undefined): string {
  const parsedEntity = parseActivityFeedEntity(entity)

  if (!parsedEntity) return ''

  switch (parsedEntity.__typename) {
    case 'BillingContact':
    case 'User': {
      const fullName = `${parsedEntity.firstName ?? ''} ${parsedEntity.lastName ?? ''}`.trim()
      return fullName || formatEntityReference(parsedEntity.id) || ''
    }
    case 'CompanyApiToken':
      return parsedEntity.token ?? ''
    case 'AdCopy':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'AdGroup':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'AdGroupBudgetPlan':
      return labelFromName(parsedEntity.plannedDate, parsedEntity.id)
    case 'AdGroupDirectoryPreset':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'PublisherExclusionPreset':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'AdTemplateBanner':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'AdTemplateNative':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'AdTemplateWeb':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Advertiser':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Agency':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Campaign':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'CampaignProgrammatic':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Position':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Publisher':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Reseller':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'SegmentBehavioural':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'SegmentCrm':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'TradingDesk':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    case 'Website':
      return labelFromName(parsedEntity.name, parsedEntity.id)
    default:
      return ''
  }
}

export type ActivityFeedMainEntityContext = Pick<ActivityFeedPartsFragment, 'entity' | 'company'> & {
  entityType?: ActivityFeedPartsFragment['entityType']
  entityId?: ActivityFeedPartsFragment['entityId']
  items?: Array<ActivityFeedPartsFragment>
}

export function getActivityFeedMainEntityDisplay(feed: ActivityFeedMainEntityContext): {
  label: string
  url?: string
} {
  const parsedEntity = parseActivityFeedEntity(feed.entity)
  const labelFromEntity = getActivityFeedEntityLabel(parsedEntity)

  if (labelFromEntity) {
    return {
      label: labelFromEntity,
      url: getActivityFeedEntityUrl({ entity: parsedEntity, company: feed.company }),
    }
  }

  const fallbackLabel = formatEntityReference(feed.entityId ?? feed.items?.[0]?.entityId)

  return {
    label: fallbackLabel ?? '',
    url: undefined,
  }
}

export function getActivityFeedEntityUrl(
  feed: Pick<ActivityFeedPartsFragment, 'company'> & {
    entity: ActivityFeedResolvedEntity | null | undefined
  }
): string | undefined {
  const { company } = feed
  const entity = parseActivityFeedEntity(feed.entity)

  if (!entity || !company) return undefined

  const companyBaseUrl = CompanyRouteBuilder(company.slug, company.type)

  switch (entity.__typename) {
    case 'User':
      return `${companyBaseUrl}/users/${entity.id}`
    case 'AdGroup': {
      const advertiserSlug = entity.campaign?.advertiser?.slug ?? advertiserSlugFromCompany(company)
      if (!advertiserSlug) return undefined

      return AdGroupRouteBuilder(advertiserSlug, String(entity.id))
    }
    case 'Website': {
      const publisherSlug = entity.publisher?.slug ?? publisherSlugFromCompany(company)
      if (!publisherSlug) return undefined

      return WebsiteRouteBuilder(publisherSlug, String(entity.id))
    }
    case 'Position': {
      const publisherSlug = entity.website?.publisher?.slug ?? publisherSlugFromCompany(company)
      if (!publisherSlug) return undefined

      return PositionRouteBuilder(publisherSlug, String(entity.id))
    }
    case 'Campaign': {
      const advertiserSlug = entity.advertiser?.slug ?? advertiserSlugFromCompany(company)
      if (!advertiserSlug) return undefined

      return CampaignRouteBuilder(advertiserSlug, String(entity.id))
    }
    case 'CampaignProgrammatic': {
      const tradingDeskSlug = entity.tradingDesk?.slug ?? tradingDeskSlugFromCompany(company)
      if (!tradingDeskSlug) return undefined

      return CampaignProgrammaticRouteBuilder(tradingDeskSlug, String(entity.id))
    }
    case 'Advertiser':
    case 'Publisher':
    case 'Agency':
    case 'Reseller':
    case 'TradingDesk':
      return CompanyRouteBuilder(entity.slug, entity.type)
    case 'AdGroupDirectoryPreset':
      return `${companyBaseUrl}/directory-presets/${entity.id}`
    case 'BillingContact':
      return `${companyBaseUrl}/details`
    case 'CompanyApiToken':
      return `${companyBaseUrl}/api`
    case 'AdTemplateBanner':
    case 'AdTemplateNative':
    case 'AdTemplateWeb': {
      const publisherSlug = entity.position?.website?.publisher?.slug ?? publisherSlugFromCompany(company)
      const positionId = entity.position?.id
      if (!publisherSlug || positionId == null) return undefined

      return AdTemplateRouteBuilder(publisherSlug, String(positionId), String(entity.id))
    }
    case 'AdCopy': {
      const advertiserSlug = entity.adGroup?.campaign?.advertiser?.slug ?? advertiserSlugFromCompany(company)
      const adGroupId = entity.adGroup?.id
      if (!advertiserSlug || adGroupId == null) return undefined

      return AdCopyRouteBuilder(advertiserSlug, String(adGroupId), String(entity.id))
    }
    case 'SegmentCrm':
    case 'SegmentBehavioural':
      return company.type === CompanyType.Advertiser ? `${companyBaseUrl}/dashboard` : undefined
    default:
      return undefined
  }
}
